/**
 * DORA Score Calculation
 * DORA = 40% Self-Assessment + 30% Controls + 30% Processes
 * Controls/Processes: IMPLEMENTED=100, PARTIAL=50, NOT_IMPLEMENTED=0, N_A=excluded
 */

export type DoraControlStatus = 'IMPLEMENTED' | 'PARTIAL' | 'NOT_IMPLEMENTED' | 'N_A';

const STATUS_TO_PERCENT: Record<DoraControlStatus, number | null> = {
  IMPLEMENTED: 100,
  PARTIAL: 50,
  NOT_IMPLEMENTED: 0,
  N_A: null,
};

function normalizeStatus(s: string | undefined | null): DoraControlStatus | null {
  if (!s || typeof s !== 'string') return null;
  const u = s.toUpperCase().replace(/\s+/g, '_');
  if (u === 'IMPLEMENTED' || u === 'PARTIAL' || u === 'NOT_IMPLEMENTED' || u === 'N_A' || u === 'NA') {
    return (u === 'NA' ? 'N_A' : u) as DoraControlStatus;
  }
  return null;
}

/**
 * Compute DORA Controls score from control answers.
 */
export function computeDoraControlsScore(controlsAnswers: Record<string, string>): {
  score: number;
  breakdown: { implemented: number; partial: number; notImplemented: number; na: number; totalNonNA: number };
} {
  const breakdown = { implemented: 0, partial: 0, notImplemented: 0, na: 0, totalNonNA: 0 };
  let sum = 0;

  for (const raw of Object.values(controlsAnswers)) {
    const status = normalizeStatus(raw);
    if (!status) continue;

    if (status === 'N_A') {
      breakdown.na++;
      continue;
    }

    breakdown.totalNonNA++;
    const pct = STATUS_TO_PERCENT[status];
    if (pct !== null) sum += pct;

    if (status === 'IMPLEMENTED') breakdown.implemented++;
    else if (status === 'PARTIAL') breakdown.partial++;
    else breakdown.notImplemented++;
  }

  const score = breakdown.totalNonNA > 0
    ? Math.round((sum / (breakdown.totalNonNA * 100)) * 100)
    : 0;

  return { score, breakdown };
}

/**
 * Compute DORA total score: 40% SA + 30% Controls + 30% Processes
 */
export function computeDoraScore(params: {
  selfAssessmentScore: number | null | undefined;
  controlsScore: number | null | undefined;
  processesScore: number | null | undefined;
}): number {
  const sa = typeof params.selfAssessmentScore === 'number' && Number.isFinite(params.selfAssessmentScore)
    ? Math.max(0, Math.min(100, params.selfAssessmentScore))
    : 0;
  const ctrl = typeof params.controlsScore === 'number' && Number.isFinite(params.controlsScore)
    ? Math.max(0, Math.min(100, params.controlsScore))
    : 0;
  const proc = typeof params.processesScore === 'number' && Number.isFinite(params.processesScore)
    ? Math.max(0, Math.min(100, params.processesScore))
    : 0;

  if (sa === 0 && ctrl === 0 && proc === 0) return 0;
  return Math.round(0.4 * sa + 0.3 * ctrl + 0.3 * proc);
}
